import { useState } from "react";
import { CheckCircle2, Clock, Mail, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

/** E-mails adicionais do usuário — só servem pra reivindicar convite de equipe endereçado a
 *  eles depois de verificados. Login continua sempre pelo e-mail principal da conta. */
export function MyEmailsSection() {
  const utils = trpc.useUtils();
  const emailsQuery = trpc.me.listEmails.useQuery();
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const addEmail = trpc.me.addEmail.useMutation({
    onSuccess: () => {
      toast.success("Enviamos um link de confirmação pro e-mail adicionado.");
      utils.me.listEmails.invalidate();
      setAdding(false);
      setNewEmail("");
    },
    onError: (err) => toast.error(err.message || "Erro ao adicionar e-mail."),
  });

  const removeEmail = trpc.me.removeEmail.useMutation({
    onSuccess: () => {
      toast.success("E-mail removido.");
      utils.me.listEmails.invalidate();
    },
    onError: (err) => toast.error(err.message || "Erro ao remover e-mail."),
  });

  return (
    <div className="space-y-3 border-t border-zinc-100 pt-5 dark:border-border">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Meus e-mails
        </p>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-brand-700 hover:underline"
          >
            <Plus className="h-3.5 w-3.5" />
            Adicionar e-mail
          </button>
        )}
      </div>
      <p className="text-[11px] text-zinc-500">
        Um e-mail adicional verificado serve só pra você aceitar convites de equipe endereçados a
        ele (ex: e-mail corporativo) sem precisar criar outra conta. O login continua sempre pelo
        seu e-mail principal.
      </p>

      {adding && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (newEmail.trim()) addEmail.mutate({ email: newEmail.trim() });
          }}
          className="flex items-center gap-2 rounded-lg border border-zinc-200 p-2"
        >
          <Mail className="h-4 w-4 shrink-0 text-zinc-400" />
          <input
            type="email"
            required
            autoFocus
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="nome@empresa.com"
            className="min-w-0 flex-1 bg-transparent text-sm text-zinc-900 outline-none placeholder:text-zinc-400"
          />
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setNewEmail("");
            }}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="submit"
            disabled={addEmail.isPending}
            className="shrink-0 rounded-md bg-brand-600 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            Enviar link
          </button>
        </form>
      )}

      <div className="space-y-1.5">
        {(emailsQuery.data ?? []).map((e) => (
          <div
            key={e.id}
            className="flex items-center justify-between gap-2 rounded-lg border border-zinc-200 px-3 py-2"
          >
            <div className="flex min-w-0 items-center gap-2">
              {e.verifiedAt ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
              ) : (
                <Clock className="h-3.5 w-3.5 shrink-0 text-amber-500" />
              )}
              <span className="truncate text-sm text-zinc-800">{e.email}</span>
              <span
                className={
                  e.verifiedAt
                    ? "shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-600"
                    : "shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-600"
                }
              >
                {e.verifiedAt ? "Verificado" : "Aguardando verificação"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => removeEmail.mutate({ id: e.id })}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Remover e-mail"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
